/**
 * Extract non-artifact files referenced in conversation messages.
 *
 * "Other files" are the files that flow through the chat but aren't agent-
 * generated artifacts. Two sources:
 *
 *   1. **User uploads** — drop / paste / file-picker attachments. The chat
 *      input bar appends them to the outgoing user message as plain text:
 *      single → ``\n\nAttached file: <path>``
 *      multi  → ``\n\nAttached files:\n- <path1>\n- <path2>``
 *      We parse those suffixes back out of stored user messages.
 *
 *   2. **Tool-referenced files** — file paths the agent passed to known
 *      file-handling tools (``Read``, ``Write``, ``Edit``, ``add_artifact``,
 *      ``render_ui`` …). Pulled from each tool_use message's ``input``.
 *
 * Files already represented by an entry in the artifacts list (matched by
 * basename, since the frontend never sees the artifact's absolute path)
 * are filtered out so the same file doesn't appear in both "Artifacts"
 * and "Other Files" sections.
 */

import type { Message } from "@/api/types";

export interface OtherFile {
  /** Absolute filesystem path — the canonical identifier. */
  path: string;
  /** Basename, e.g. ``transcript.pdf``. */
  name: string;
  /** Lower-case extension without the dot, or empty string when missing. */
  ext: string;
  /** Whether the user attached it or the agent referenced it via a tool. */
  source: "upload" | "tool";
  /** Tool that referenced this path (only set when ``source === "tool"``). */
  toolName?: string;
  /** ``created_at`` of the originating message — drives sort order. */
  createdAt: string;
}

/** Tools whose ``input`` carries a single ``file_path``/``path`` string.
 *
 * ``render_ui`` is deliberately *absent*: interactive widgets get their own
 * "Widgets" section (see ``extractWidgets``), sourced from the persisted
 * ``genui`` messages rather than the tool input — that gives us the agent's
 * widget title and a stable id, and keeps widgets out of "Other Files". */
const SINGLE_PATH_TOOLS = new Set([
  "Read",
  "Write",
  "Edit",
  "MultiEdit",
  "NotebookEdit",
  "mcp__cowork__add_artifact",
  "mcp__cowork__upload_user_document",
]);

/** Common keys for a single file-path input across the tools above. */
const SINGLE_PATH_KEYS = ["file_path", "filepath", "path", "filename"];

/** Common keys whose value is a list of file paths. */
const MULTI_PATH_KEYS = ["file_paths", "filepaths", "paths"];

function getMessageText(msg: Message): string {
  try {
    const blocks = JSON.parse(msg.content_json) as Array<{ type?: string; text?: string }>;
    return blocks
      .map((b) => (b?.type === "text" && typeof b.text === "string" ? b.text : ""))
      .join("\n");
  } catch {
    return "";
  }
}

function extractUploadsFromUserText(text: string): string[] {
  const paths: string[] = [];

  // Single attachment: "\n\nAttached file: <path>"
  // Match to end-of-line (or end-of-string) to avoid swallowing trailing
  // markdown that ChatInput sometimes adds beneath.
  for (const m of text.matchAll(/\n\nAttached file:\s*(.+?)(?:\n|$)/g)) {
    const p = m[1]?.trim();
    if (p) paths.push(p);
  }

  // Multi-attachment block: "\n\nAttached files:\n- <p1>\n- <p2>\n…"
  // We grab every "- <path>" line until a blank line or end-of-string.
  const multi = text.match(/\n\nAttached files:\n((?:- .+(?:\n|$))+)/);
  if (multi?.[1]) {
    for (const line of multi[1].split("\n")) {
      const p = line.replace(/^-\s+/, "").trim();
      if (p) paths.push(p);
    }
  }

  return paths;
}

function extractPathsFromToolInput(input: unknown): string[] {
  if (!input || typeof input !== "object") return [];
  const obj = input as Record<string, unknown>;
  const out: string[] = [];

  for (const key of SINGLE_PATH_KEYS) {
    const v = obj[key];
    if (typeof v === "string" && v.trim()) out.push(v.trim());
  }
  for (const key of MULTI_PATH_KEYS) {
    const v = obj[key];
    if (Array.isArray(v)) {
      for (const item of v) {
        if (typeof item === "string" && item.trim()) out.push(item.trim());
      }
    }
  }
  return out;
}

export function basenameOf(p: string): string {
  // Handle both POSIX and Windows separators — paths from /api/files/pick
  // come back in the platform's native form.
  const lastSep = Math.max(p.lastIndexOf("/"), p.lastIndexOf("\\"));
  return lastSep >= 0 ? p.slice(lastSep + 1) : p;
}

function extOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

/**
 * Walk the conversation's messages and return every non-artifact file
 * reference. Deduplicated by absolute path; earlier mentions win so the
 * ``createdAt`` reflects when the file first entered the conversation.
 *
 * @param messages          full message list for the conversation
 * @param artifactFilenames basenames of files already shown in Artifacts
 *                          — used to suppress duplicates in Other Files
 */
export function extractOtherFiles(
  messages: Message[],
  artifactFilenames: Set<string>,
): OtherFile[] {
  const seen = new Map<string, OtherFile>();

  const record = (path: string, source: "upload" | "tool", msg: Message): void => {
    const name = basenameOf(path);
    if (!name || artifactFilenames.has(name) || seen.has(path)) return;
    seen.set(path, {
      path,
      name,
      ext: extOf(name),
      source,
      ...(source === "tool" && msg.tool_name ? { toolName: msg.tool_name } : {}),
      createdAt: msg.created_at,
    });
  };

  for (const msg of messages) {
    if (msg.role === "user") {
      const text = getMessageText(msg);
      for (const path of extractUploadsFromUserText(text)) record(path, "upload", msg);
      continue;
    }

    if (msg.role === "tool_use" && msg.tool_name && SINGLE_PATH_TOOLS.has(msg.tool_name)) {
      let input: unknown = null;
      try {
        const blocks = JSON.parse(msg.content_json) as Array<{ type?: string; input?: unknown }>;
        const useBlock = blocks.find((b) => b?.type === "tool_use");
        input = useBlock?.input ?? null;
      } catch {
        continue;
      }
      for (const path of extractPathsFromToolInput(input)) record(path, "tool", msg);
    }
  }

  return Array.from(seen.values());
}

/** An interactive ``render_ui`` widget surfaced in the Files panel. */
export interface Widget {
  /** The genui message id — also the ``genui_id``. Used to save the widget
   *  as an artifact via ``POST /api/shortlists/artifacts/from-widget``. */
  genuiId: string;
  /** Absolute path to the backing HTML file — the de-dup key. */
  path: string;
  /** Basename, e.g. ``revenue-chart.html``. */
  name: string;
  /** Agent-provided widget title; falls back to the basename. */
  title: string;
  /** Lower-case extension without the dot (normally ``html``). */
  ext: string;
  /** ``created_at`` of the genui message — drives sort order. */
  createdAt: string;
}

/**
 * Walk the conversation's messages and return every inline ``render_ui``
 * widget, deduplicated by backing path (first mention wins).
 *
 * Widgets are sourced from persisted ``genui`` messages — not the
 * ``render_ui`` tool input — because the message carries the agent's title
 * and its id *is* the ``genui_id`` we need to save it. The id is taken from
 * ``msg.id`` rather than the block: the server persists the genui block
 * without a ``genui_id`` field (it lives as the message id), so a reloaded
 * widget's block has none.
 *
 * Widgets whose file is already registered as an artifact are filtered out
 * (matched by basename, mirroring ``extractOtherFiles``) so a saved widget
 * shows only under "Artifacts", never twice.
 *
 * @param messages          full message list for the conversation
 * @param artifactFilenames basenames of files already shown in Artifacts
 */
export function extractWidgets(
  messages: Message[],
  artifactFilenames: Set<string>,
): Widget[] {
  const seen = new Map<string, Widget>();

  for (const msg of messages) {
    if (msg.role !== "genui") continue;

    let block: { title?: string; file_path?: string } | undefined;
    try {
      const blocks = JSON.parse(msg.content_json) as Array<{
        type?: string;
        title?: string;
        file_path?: string;
      }>;
      block = blocks.find((b) => b?.type === "genui");
    } catch {
      continue;
    }

    const path = block?.file_path?.trim();
    if (!path) continue;
    const name = basenameOf(path);
    if (!name || artifactFilenames.has(name) || seen.has(path)) continue;

    seen.set(path, {
      genuiId: msg.id,
      path,
      name,
      title: block?.title?.trim() || name,
      ext: extOf(name),
      createdAt: msg.created_at,
    });
  }

  return Array.from(seen.values());
}
